// src/domains/chat/pages/ChatPage.jsx
// Chat em tempo real cliente ↔ profissional, vinculado a um agendamento.
// Acessível apenas quando existe agendamento — sem agendamento não há chat.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { chatsService } from 'infrastructure/firebase';
import { Avatar } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';
import { formatDate } from 'shared/utils/helpers';

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  myBubble: '#6B5876', myBubbleText: '#fff',
  otherBubble: '#F0EDF3', otherBubbleText: '#3D3447',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

// Converte timestamp Firestore para "HH:MM"
function tsToTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// Converte timestamp para data amigável
function tsToDateLabel(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const today = new Date();
  const diff  = Math.floor((today - d) / 86400000);
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

// Agrupa mensagens por dia para exibir separadores de data
function groupByDay(messages) {
  const groups = [];
  let lastDay   = '';
  for (const msg of messages) {
    const day = tsToDateLabel(msg.criadaEm);
    if (day !== lastDay) {
      groups.push({ type: 'divider', label: day });
      lastDay = day;
    }
    groups.push({ type: 'message', ...msg });
  }
  return groups;
}

export default function ChatPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { userProfile }  = useAuth();
  const { appointments } = useApp();

  const [chatId, setChatId]     = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const unsubRef   = useRef(null);

  // Encontra o agendamento correspondente
  const appointment = appointments.find(a => a.id === appointmentId);

  // Determina o "outro lado" da conversa
  const isClient = userProfile?.role === 'client';
  const otherName = isClient ? appointment?.professionalName : appointment?.clientName;
  const otherColor = isClient ? 'lavender' : 'rose';

  // Inicializa o chat (cria se não existir)
  useEffect(() => {
    if (!appointment || !userProfile?.id) return;

    chatsService.createOrGet({
      appointmentId,
      clientId:       appointment.clientId,
      professionalId: appointment.professionalId,
    }).then(chat => {
      setChatId(chat.id);
      setLoading(false);
    }).catch(err => {
      console.error('[Chat] Erro ao criar/obter chat:', err);
      setLoading(false);
    });
  }, [appointment, userProfile, appointmentId]);

  // Listener de mensagens em tempo real
  useEffect(() => {
    if (!chatId) return;
    unsubRef.current = chatsService.onSnapshotMsgs(
      chatId,
      (msgs) => setMessages(msgs),
      (err) => console.error('[Chat] Listener erro:', err)
    );
    return () => unsubRef.current?.();
  }, [chatId]);

  // Scroll automático ao receber nova mensagem
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    const content = text.trim();
    if (!content || !chatId || !userProfile?.id || sending) return;
    setSending(true);
    setText('');
    try {
      await chatsService.sendMessage(chatId, {
        senderId: userProfile.id,
        conteudo: content,
      });
    } catch (err) {
      console.error('[Chat] Erro ao enviar:', err);
      setText(content); // restaura texto em caso de erro
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [text, chatId, userProfile, sending]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!appointment) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', fontFamily: T.sans, color: T.muted, gap: 12 }}>
      <div style={{ fontSize: 48 }}>💬</div>
      <div style={{ fontSize: 16, color: T.text }}>Agendamento não encontrado</div>
      <div style={{ fontSize: 13 }}>Este chat está vinculado a um agendamento que não foi localizado.</div>
      <button onClick={() => navigate(-1)} style={{ marginTop: 8, background: 'none', border: 'none',
        color: T.brand, cursor: 'pointer', fontSize: 14, fontFamily: T.sans }}>← Voltar</button>
    </div>
  );

  const grouped = groupByDay(messages);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 116px)' : 'calc(100vh - 88px)',
      fontFamily: T.sans, maxWidth: 700, margin: '0 auto' }}>

      {/* ── Cabeçalho ─────────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${T.border}`,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: T.muted, fontSize: 20, padding: '0 4px', lineHeight: 1 }}>
          ‹
        </button>
        <Avatar name={otherName || '?'} color={otherColor} size="md" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: T.text, lineHeight: 1.2 }}>{otherName}</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
            {appointment.procedureName} · {formatDate(appointment.date)} às {appointment.time}
          </div>
        </div>
        <div style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 500,
          background: appointment.status === 'confirmed' ? '#E7F4ED' : '#F0EDF3',
          color:      appointment.status === 'confirmed' ? '#2D5F43' : T.text2 }}>
          {appointment.status === 'confirmed' ? '✓ Confirmado' :
           appointment.status === 'cancelled'  ? 'Cancelado' : 'Pendente'}
        </div>
      </div>

      {/* ── Área de mensagens ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex',
        flexDirection: 'column', gap: 4, background: T.s2 }}>

        {loading ? (
          <div style={{ textAlign: 'center', color: T.muted, padding: '40px 0', fontSize: 13 }}>
            Carregando conversa...
          </div>
        ) : grouped.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 16px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
            <div style={{ fontSize: 14, color: T.text, marginBottom: 6 }}>Nenhuma mensagem ainda</div>
            <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
              Use este chat para combinar detalhes do atendimento com {otherName}.
            </div>
          </div>
        ) : (
          grouped.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key={`d-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 10,
                  margin: '8px 0', userSelect: 'none' }}>
                  <div style={{ flex: 1, height: 1, background: T.border }} />
                  <span style={{ fontSize: 11, color: T.muted, whiteSpace: 'nowrap' }}>{item.label}</span>
                  <div style={{ flex: 1, height: 1, background: T.border }} />
                </div>
              );
            }

            const isMine = item.senderId === userProfile?.id;
            return (
              <div key={item.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start',
                marginBottom: 2 }}>
                <div style={{ maxWidth: '75%' }}>
                  <div style={{
                    padding: '9px 13px', borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: isMine ? T.myBubble : T.otherBubble,
                    color:      isMine ? T.myBubbleText : T.otherBubbleText,
                    fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word',
                  }}>
                    {item.conteudo}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 3,
                    textAlign: isMine ? 'right' : 'left' }}>
                    {tsToTime(item.criadaEm)}
                    {isMine && item.lida && <span style={{ marginLeft: 4 }}>✓✓</span>}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Input de mensagem ──────────────────────────────────────────────────── */}
      {appointment.status !== 'cancelled' ? (
        <div style={{ background: '#fff', borderTop: `1px solid ${T.border}`,
          padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
          <textarea
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Mensagem... (Enter para enviar)"
            rows={1}
            style={{ flex: 1, padding: '10px 14px', border: `1.5px solid ${T.border}`,
              borderRadius: 22, fontSize: 14, fontFamily: T.sans, color: T.text,
              outline: 'none', resize: 'none', background: T.s2, lineHeight: 1.5,
              maxHeight: 100, overflowY: 'auto' }}
            onInput={e => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
          />
          <button onClick={handleSend} disabled={!text.trim() || sending}
            style={{ width: 42, height: 42, flexShrink: 0, borderRadius: '50%',
              background: text.trim() && !sending ? T.brand : T.border,
              border: 'none', cursor: text.trim() && !sending ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, transition: 'background 0.15s' }}>
            {sending ? '⏳' : '➤'}
          </button>
        </div>
      ) : (
        <div style={{ background: T.s2, borderTop: `1px solid ${T.border}`,
          padding: '12px 16px', textAlign: 'center', fontSize: 13, color: T.muted, flexShrink: 0 }}>
          Este agendamento foi cancelado — o chat está encerrado.
        </div>
      )}
    </div>
  );
}
